import type { FC } from "react";
import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Dropdown, ListItemKeywordGroup } from "@similarweb/ui-components";

import { tableActionsCreator } from "app/actions/tableActions";
import { showSuccessToast } from "app/actions/toast_actions";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { StyledLink } from "pro-features/styled components/StyledLink/src/StyledLink";

import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { keywordGroupsQueryClient, keywordGroupsTransforms, KEYWORD_GROUP_PREFIX } from "app/services/keyword-groups";

import {
    CTA_NAVIGATION_STATE,
    GOOGLE_SEARCH_ENGINE,
    TABLE_SELECTION_KEY,
    TABLE_SELECTION_PROPERTY,
} from "./keywordIdeasConsts";

import { translate } from "app/filters/i18nFilter";
import { useCreateOrUpdateKeywordGroup, useKeywordGroups } from "app/services/keyword-groups/hooks";

import { validateNewKeywords } from "./keywordIdeasUtils";

import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import { allTrackers } from "app/services/track/track";

const FooterContainer = styled(RightFlexRow)`
    box-sizing: border-box;
    padding-right: 8px;
    height: 51px;
    border-top: 1px solid ${colorsPalettes.carbon[50]};
    > :last-child {
        width: auto !important;
    }
`;

const SeeMoreButtonContainer = styled(StyledLink)`
    font-size: 14px;
`;

interface IAddToKeywordListButton {
    isLoading: boolean;
    isDisabled: boolean;
}

const AddToKeywordListButton: FC<IAddToKeywordListButton> = ({ isLoading, isDisabled }) => {
    return (
        <Button
            isLoading={isLoading}
            isDisabled={isDisabled}
            type={buttonTypes.PRIMARY}
            label={translate("keyword.analysis.overview.page.v2.keyword_ideas.add_to_list.cta")}
            onClick={() => {
                allTrackers.dispatchTrackEvent(
                    "keyword.analysis.overview.page.v2.keyword_ideas.add_to_list.cta",
                    "click",
                    "keyword.analysis.overview.page.v2.keyword_ideas.add_to_list.cta",
                );
            }}
        />
    );
};

export const KeywordIdeasFooter: FC = () => {
    const dispatch = useDispatch();
    const { params } = useCurrentStateAndParams();
    const { keyword, duration, country, tab: selectedTab } = params;
    const { data: keywordGroups } = useKeywordGroups();
    const keywordLists = keywordGroupsTransforms.toDropDown(keywordGroups);
    const selectedRows = useSelector(getTableSelectedItems(TABLE_SELECTION_KEY));
    const { clearAllSelectedRows } = tableActionsCreator(TABLE_SELECTION_KEY, TABLE_SELECTION_PROPERTY);
    const [isLoading, setIsLoading] = useState(false);
    const createOrUpdateKeywordGroup = useCreateOrUpdateKeywordGroup();

    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword);

    const link = proNavigator.href(CTA_NAVIGATION_STATE, {
        keyword,
        mtd: false,
        webSource: devicesTypes.TOTAL,
        tab: selectedTab,
        country,
        duration,
        searchEngine: GOOGLE_SEARCH_ENGINE,
    });

    const reloadPage = (params = {}) => {
        proNavigator.go(proNavigator.current().name, params, { reload: true });
    };

    const handleFailedAddToList = (message: string) => {
        dispatch(showSuccessToast(message));
        dispatch(clearAllSelectedRows());
        setIsLoading(false);
    };

    const handleSuccessfulAddToList = (
        nonDuplicateKeywordsListLength: number,
        groupName: string,
        groupId: string,
        showLink: boolean,
    ) => {
        allTrackers.dispatchTrackEvent(
            "Add To Keyword Group",
            "click",
            `add items to keyword group ${groupName || ""}`,
        );

        let toast;
        if (showLink) {
            toast = getToastItemComponent({
                text: translate("KeywordAnalysis.keywordgroup.wizard.toast.modified", {
                    keyword: `"${groupName}"`,
                }),
                linkText: translate("KeywordAnalysis.keywordgroup.wizard.toast.created.cta"),
                onClick: () => reloadPage({ keyword: `${KEYWORD_GROUP_PREFIX}${groupId}` }),
            });
        } else {
            toast = translate("keyword.analysis.overview.page.v2.keyword_ideas.add_to_list.toast", {
                count: nonDuplicateKeywordsListLength,
                listName: `"${groupName}"`,
            });
        }

        dispatch(showSuccessToast(toast));
    };

    const onKeywordListClick = async ({ id }: { id: string }) => {
        setIsLoading(true);
        const selectedKeywordGroup = await keywordGroupsQueryClient.getGroupById(id);
        const selectedKeywordGroupList = selectedKeywordGroup.Keywords;
        const keywordsToAddToGroup = selectedRows.map((row) => row.keyword);

        const { isValid, message, duplicates } = validateNewKeywords(selectedKeywordGroupList, keywordsToAddToGroup);

        if (!isValid) {
            handleFailedAddToList(message);
            return;
        }

        const nonDuplicateKeywords = keywordsToAddToGroup.filter((keyword) => !duplicates.includes(keyword));

        try {
            const updatedGroup = {
                ...selectedKeywordGroup,
                Keywords: [...selectedKeywordGroupList, ...nonDuplicateKeywords],
            };
            const group = await createOrUpdateKeywordGroup.mutateAsync(updatedGroup);
            const isSelectedKeywordGroupSameAsPageKeywordGroup =
                keywordGroupsTransforms.getGroupIdFromUrlParam(keyword) === group?.Id;

            handleSuccessfulAddToList(
                nonDuplicateKeywords.length,
                group?.Name,
                group?.Id,
                !isSelectedKeywordGroupSameAsPageKeywordGroup,
            );

            if (isSelectedKeywordGroupSameAsPageKeywordGroup) {
                reloadPage();
            }
        } catch (error) {
            Sentry.error(`Error adding keywords to group in keywrod ideas table for keyword analysis overview`, error);
        } finally {
            dispatch(clearAllSelectedRows());
            setIsLoading(false);
        }
    };

    return (
        <FooterContainer>
            <SeeMoreButtonContainer href={link}>
                <Button
                    type={buttonTypes.FLAT}
                    label={translate("keyword.analysis.overview.page.v2.keyword_ideas.cta")}
                    onClick={() => {
                        allTrackers.dispatchTrackEvent("Internal Link", "click", "KeywordsIdeas.Keywords/see_more");
                    }}
                />
            </SeeMoreButtonContainer>
            {isKeywordGroup ? (
                <Dropdown
                    onClick={onKeywordListClick}
                    width={368}
                    hasSearch={true}
                    dropdownPopupHeight={360}
                    closeOnItemClick={true}
                    itemsComponent={ListItemKeywordGroup}
                    searchPlaceHolder={translate(
                        "keyword.analysis.overview.page.v2.keyword_ideas.add_to_list.search.placeholder",
                    )}
                >
                    {[
                        <AddToKeywordListButton
                            isLoading={isLoading}
                            isDisabled={!selectedRows?.length}
                        />,
                        ...keywordLists,
                    ]}
                </Dropdown>
            ) : null}
        </FooterContainer>
    );
};
